import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@mhj/shared/auth";

/**
 * Layout for the applicant app.
 *
 * Built for a phone held in one hand on a weak connection: large touch
 * targets, a single column, no hover-only affordances.
 */
export default function EmpowermentShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-full px-4 py-2 text-sm font-medium transition ${
      isActive ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-blue-50"
    }`;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="min-w-0">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-blue-600">
              Jantabo Impact
            </p>
            <p className="truncate text-base font-semibold text-slate-900">
              Empowerment Programme
            </p>
          </Link>

          <nav className="hidden items-center gap-1 sm:flex">
            <NavLink to="/status" className={navClass}>
              Check status
            </NavLink>
            <NavLink to="/results" className={navClass}>
              Results
            </NavLink>
            {user ? (
              <>
                {user.role === "field_officer" || user.role === "super_admin" ? (
                  <NavLink to="/capture" className={navClass}>
                    Capture
                  </NavLink>
                ) : null}
                <NavLink to="/dashboard" className={navClass}>
                  My application
                </NavLink>
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="rounded-full px-4 py-2 text-sm text-slate-500 transition hover:text-slate-900"
                >
                  Log out
                </button>
              </>
            ) : (
              <NavLink to="/login" className={navClass}>
                Sign in
              </NavLink>
            )}
          </nav>

          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-slate-700 sm:hidden"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              {menuOpen ? (
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>

        {menuOpen && (
          <nav className="mx-auto grid max-w-3xl gap-1 border-t border-slate-200 px-4 py-3 sm:hidden">
            <NavLink to="/status" className={navClass} onClick={() => setMenuOpen(false)}>
              Check status
            </NavLink>
            <NavLink to="/results" className={navClass} onClick={() => setMenuOpen(false)}>
              Results
            </NavLink>
            {user ? (
              <>
                {user.role === "field_officer" || user.role === "super_admin" ? (
                  <NavLink to="/capture" className={navClass} onClick={() => setMenuOpen(false)}>
                    Capture
                  </NavLink>
                ) : null}
                <NavLink to="/dashboard" className={navClass} onClick={() => setMenuOpen(false)}>
                  My application
                </NavLink>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); void logout(); }}
                  className="rounded-full px-4 py-2 text-left text-sm text-slate-500"
                >
                  Log out
                </button>
              </>
            ) : (
              <NavLink to="/login" className={navClass} onClick={() => setMenuOpen(false)}>
                Sign in
              </NavLink>
            )}
          </nav>
        )}
      </header>

      <main key={location.pathname} className="mx-auto max-w-3xl px-4 py-6 pb-20">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-6 text-xs text-slate-500">
          <p className="font-medium text-slate-700">
            Jantabo Impact Empowerment Programme
          </p>
          <p className="mt-1">
            Open to residents of Lapai Local Government, Niger State.
          </p>
          <p className="mt-2">
            Applying is free. Nobody is authorised to charge you for an
            application, and no member of staff will ask you for money.
          </p>
        </div>
      </footer>
    </div>
  );
}
